import type {
  BulkPhotoAction,
  Photo,
  PhotoBatchPatch,
  PhotoType,
} from './types';
import { MODERATION_ACTION_TO_STATE } from './types';

export interface AdminPhotoBatchPatch {
  id: number;
  state_id: number;
  types: { id: number }[];
}

const addBestType = (types: PhotoType[]): PhotoType[] =>
  Array.from(new Set<PhotoType>([...types, 'best']));

export const buildPhotoBatchPatches = (
  photos: Photo[],
  ids: string[],
  action: BulkPhotoAction,
): PhotoBatchPatch[] => {
  const photosById = new Map(photos.map((photo) => [photo.id, photo]));

  return ids.map((id) => {
    const photo = photosById.get(id);
    if (!photo) throw new Error(`Photo ${id} is not loaded`);

    return {
      id,
      state:
        action === 'best' ? 'approved' : MODERATION_ACTION_TO_STATE[action],
      types: action === 'best' ? addBestType(photo.types) : photo.types,
    };
  });
};

export const buildAdminPhotoBatchRequest = (
  patches: PhotoBatchPatch[],
  stateNameToId: (state: PhotoBatchPatch['state']) => string | undefined,
  typeNameToId: (type: PhotoType) => string | undefined,
): AdminPhotoBatchPatch[] =>
  patches.map((patch) => {
    const stateId = stateNameToId(patch.state);
    if (!stateId) throw new Error(`Unknown photo state "${patch.state}"`);

    const types = patch.types.map((type) => {
      const typeId = typeNameToId(type);
      if (!typeId) throw new Error(`Unknown photo type "${type}"`);
      return { id: Number(typeId) };
    });

    return {
      id: Number(patch.id),
      state_id: Number(stateId),
      types,
    };
  });

export const parsePhotoBatchResult = (
  status: number,
  body: unknown,
): { failed: string[] } => {
  if (status >= 200 && status < 300) return { failed: [] };
  if (status === 422) {
    const failed = (body as { failed?: unknown } | null)?.failed;
    if (Array.isArray(failed)) return { failed: failed.map(String) };
  }
  throw new Error(`Failed to update photos (HTTP ${status})`);
};
